import React, { useEffect, useState } from "react";
import { Space, Input, Tooltip, Popconfirm, message } from "antd";
import { useDispatch } from "react-redux";

import { commonSorterForTable, serialNum, showErrorMessage } from "../../../utils";
import TableContainer from "../../../components/TableContainer";
import { getAllOnboardedVehicles } from "../../../actionsMethods/cashBookingMethods";
import { getPremiseId } from "../../../utils/worker";
import {
  fetchRbacUsers,
  updateRbacUserCognitoStatus,
} from "../../../services/staffOnboarding/rbacUsersService";
import {
  filterStaffTableRowsByPhone,
  filterStaffTableRowsByStatus,
  isStaffPhoneSearchValue,
  isStaffStatusSearchValue,
  mapRbacUsersToStaffTableRows,
  parseStaffTableSearchQuery,
} from "../../../services/staffOnboarding/rbacUserPayload";

import { QuestionCircleOutlined } from "@ant-design/icons";
import { ReactComponent as Edit_icon } from "../../../assests/images/edit-logo.svg";
import { ReactComponent as Disable_icon } from "../../../assests/images/SVG/disable.svg";
import { ReactComponent as Disable_Edit_icon } from "../../../assests/images/SVG/disableEdit.svg";

import search_icon from "../../../assests/images/search-icon.svg";
import check_mark from "../../../assests/images/SVG/check.svg";
import Smcs_Loader from "../../../assests/images/loader-new.gif";

const ViewStaffTable = ({ handleEdit = () => { }, activeKey }) => {
  const dispatch = useDispatch();
  const premiseId = getPremiseId();
  const [inputSearchValue, setInputSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [tableData, setTableData] = useState([]);
  const [pageSize, setPageSize] = useState(20);
  const [pageNum, setPageNum] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortedInfo, setSortedInfo] = useState({});

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedSearch((prev) => {
        if (prev !== inputSearchValue) {
          setPageNum(1);
        }
        return inputSearchValue;
      });
    }, 400);

    return () => clearTimeout(timer);
  }, [inputSearchValue]);

  useEffect(() => {
    if (activeKey == "2") {
      getStaffTable(pageNum, pageSize, appliedSearch);
    }
  }, [activeKey, premiseId, pageNum, pageSize, appliedSearch]);

  useEffect(() => {
    if (activeKey == "2") {
      dispatch(getAllOnboardedVehicles());
    }
  }, [activeKey]);

  const getStaffTable = async (page = 1, limit = 20, search = "") => {
    setLoading(true);
    const searchParams = parseStaffTableSearchQuery(search);
    const isPhoneSearch = isStaffPhoneSearchValue(search);
    const isStatusSearch = isStaffStatusSearchValue(search);
    const { users, pagination, error } = await fetchRbacUsers({
      page,
      limit,
      tenantName: "HUBOPS",
      ...searchParams,
    });
    setLoading(false);

    if (error) {
      showErrorMessage(error);
      setTableData([]);
      setTotalRecords(0);
      return;
    }

    let rows = mapRbacUsersToStaffTableRows(users);

    if (isPhoneSearch) {
      rows = filterStaffTableRowsByPhone(rows, search);
    }

    if (isStatusSearch) {
      rows = filterStaffTableRowsByStatus(rows, search);
    }

    setTableData(rows);
    setTotalRecords(
      isPhoneSearch || isStatusSearch
        ? rows.length
        : pagination?.total || rows.length
    );
  };

  const updateCognitoStatus = async (email, enabled) => {
    setLoading(true);
    const { message: successMessage, error } = await updateRbacUserCognitoStatus(
      email,
      enabled
    );
    setLoading(false);

    if (error) {
      showErrorMessage(error);
      return;
    }

    message.success(
      successMessage ||
      (enabled ? "Staff enabled successfully" : "Staff disabled successfully")
    );
    getStaffTable(pageNum, pageSize, appliedSearch);
  };

  const handleDisable = (email) => {
    updateCognitoStatus(email, false);
  };

  const handleEnable = (email) => {
    updateCognitoStatus(email, true);
  };

  const handleChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter);
  };

  const columns = [
    {
      title: "Sl No",
      dataIndex: "key",
      key: "key",
      align: "center",
      render: (a, b, idx) => serialNum(pageSize, pageNum, idx),
    },
    {
      title: "Emp ID",
      dataIndex: "employeeId",
      key: "employeeId",
      ellipsis: true,
      render: (text) => text || "--",
    },
    {
      title: "User name",
      dataIndex: "firstName",
      key: "firstName",
      sortOrder: sortedInfo.columnKey === "firstName" ? sortedInfo.order : null,
      sorter: (a, b) => commonSorterForTable(a, b, "firstName"),
      ellipsis: true,
      render: (text, val) => {
        return [text, val?.lastName].filter(Boolean).join(" ") || "--";
      },
    },
    {
      title: "Email",
      dataIndex: "emailId",
      key: "emailId",
      width: 260,
      ellipsis: true,
    },
    {
      title: "Phone",
      dataIndex: "contactNumber",
      key: "contactNumber",
      ellipsis: true,
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
      render: (text) => text || "--",
    },
    {
      title: "Account Status",
      dataIndex: "accountStatusLabel",
      key: "accountStatusLabel",
      align: "center",
      render: (text, record) => (
        <span style={{ color: record?.cognitoEnabled ? "#52c41a" : "#ff4d4f" }}>
          {text || (record?.cognitoEnabled ? "Active" : "Inactive")}
        </span>
      ),
    },
    {
      title: "Confirmation Status",
      dataIndex: "confirmationStatusLabel",
      key: "confirmationStatusLabel",
      align: "center",
      render: (text, record) => (
        <span style={{ color: record?.status ? "#52c41a" : "#fa8c16" }}>
          {text || (record?.status ? "Confirmed" : "Pending")}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "action",
      align: "center",
      className: "actions-col",
      fixed: "right",
      render: (_, record) => {
        return record?.cognitoEnabled ? (
          <Space size="middle">
            <Tooltip title={"Edit"} placement="bottom">
              <p
                className="pointer"
                style={{ marginBottom: 0 }}
                onClick={() => handleEdit(record?.emailId)}
              >
                <Edit_icon />
              </p>
            </Tooltip>
            <Tooltip title="Disable" placement="bottom">
              <div className="view-all-staff">
                <Popconfirm
                  title={
                    <span
                      className="test"
                      style={{
                        textTransform: "initial",
                      }}
                    >
                      Do you want to disable this Staff ?
                    </span>
                  }
                  cancelText="No"
                  okText="Yes"
                  icon={
                    <QuestionCircleOutlined
                      style={{
                        color: "red",
                      }}
                    />
                  }
                  onConfirm={() => handleDisable(record?.emailId)}
                >
                  <p className="pointer" style={{ marginBottom: 0 }}>
                    <Disable_icon />
                  </p>
                </Popconfirm>
              </div>
            </Tooltip>
          </Space>
        ) : (
          <Space size="middle">
            <p style={{ marginBottom: 0 }}>
              <Disable_Edit_icon className="pointer" />
            </p>
            <Tooltip title="Enable" placement="bottom">
              <Popconfirm
                title={
                  <span
                    className="test"
                    style={{
                      textTransform: "initial",
                    }}
                  >
                    Do you want to enable this Staff ?
                  </span>
                }
                okText="Yes"
                cancelText="No"
                icon={
                  <QuestionCircleOutlined
                    style={{
                      color: "green",
                    }}
                  />
                }
                onConfirm={() => handleEnable(record?.emailId)}
              >
                <p className="pointer">
                  <img
                    src={check_mark}
                    style={{ width: "22px", marginBottom: "10px" }}
                  />
                </p>
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  const itemRender = (_, type, originalElement) => {
    if (type === "prev") {
      return <a>Previous</a>;
    }
    if (type === "next") {
      return <a>Next</a>;
    }
    return (
      <div>
        <p>{originalElement} </p>
      </div>
    );
  };

  const onSearch = (e) => {
    setInputSearchValue(e.target.value);
  };

  const handlePageChange = (currentPage, nextPageSize) => {
    setPageNum(currentPage);
    setPageSize(nextPageSize);
  };

  return (
    <div className="master-main-container view-all-staff">
      <div className="align-center space-between">
        <div className="pl-1-5 fw-600"></div>
        <div className="text-right p-1">
          <Input
            value={inputSearchValue}
            placeholder="Search by user name, email, phone or account status"
            bordered
            allowClear
            onChange={onSearch}
            style={{ borderRadius: "5px", height: "40px", width: "400px" }}
            suffix={<img src={search_icon} alt="search" />}
          />
        </div>
      </div>
      <TableContainer
        className="table-head-sticky"
        loading={{
          indicator: (
            <img
              src={Smcs_Loader}
              style={{ height: "100px", width: "100px" }}
            />
          ),
          spinning: loading,
        }}
        columns={columns}
        defaultCurrent={1}
        onChange={handleChange}
        dataSource={tableData}
        scroll={{ x: true }}
        pagination={{
          current: pageNum,
          pageSize,
          total: totalRecords,
          showSizeChanger: true,
          itemRender: itemRender,
          onChange: handlePageChange,
        }}
        rowClassName={(record) => !record?.cognitoEnabled && "disabled-row"}
        rowKey={(record) => record?.userId || record?.staffId || record?.emailId}
      />
    </div>
  );
};

export default ViewStaffTable;
